import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Play,
  Pause,
  Maximize,
  Minimize,
  Volume2,
  VolumeX,
} from "lucide-react";
import "../styles/WalkthroughSerene.css";

const posterImg = "/Serene-Construction-Progress/walkthrough-video-thumb-vessella-serene.png";

export default function WalkthroughSerene() {
  const videoSrc = "/Serene-Construction-Progress/Vessella%20Serene.mp4";
  const videoFallbackSrc = "/Serene-Construction-Progress/Vessella Serene.mp4";

  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const controlsTimeoutRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState("00:00");
  const [duration, setDuration] = useState("00:00");
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [hasStarted, setHasStarted] = useState(false);

  // Time format helper (mm:ss)
  const formatTime = (seconds) => {
    if (isNaN(seconds)) return "00:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Play / Pause toggle
  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused || video.ended) {
      video.play().then(() => {
        setIsPlaying(true);
        setHasStarted(true);
      }).catch((err) => {
        console.warn("Autoplay/play prevented:", err);
      });
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  // Update progress & time as video plays
  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video || isNaN(video.duration)) return;
    const current = video.currentTime;
    const total = video.duration;
    setProgress((current / total) * 100);
    setCurrentTime(formatTime(current));
  };

  // Loaded metadata handler
  const handleLoadedMetadata = () => {
    const video = videoRef.current;
    if (!video) return;
    setDuration(formatTime(video.duration));
  };

  // Seek / scrub video timeline
  const handleSeek = (e) => {
    const video = videoRef.current;
    if (!video || !video.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    const clampedPos = Math.max(0, Math.min(1, pos));
    video.currentTime = clampedPos * video.duration;
    setProgress(clampedPos * 100);
  };

  // Volume & Mute handling
  const handleVolumeChange = (e) => {
    const val = parseFloat(e.target.value);
    setVolume(val);
    if (videoRef.current) {
      videoRef.current.volume = val;
      videoRef.current.muted = val === 0;
      setIsMuted(val === 0);
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    if (isMuted) {
      video.muted = false;
      setIsMuted(false);
      video.volume = volume || 0.8;
    } else {
      video.muted = true;
      setIsMuted(true);
    }
  };

  // Fullscreen toggle (HTML5 Fullscreen API with vendor fallbacks)
  const toggleFullscreen = async (e) => {
    if (e) e.stopPropagation();
    const container = containerRef.current;
    const video = videoRef.current;
    if (!container) return;

    try {
      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        if (container.requestFullscreen) {
          await container.requestFullscreen();
        } else if (container.webkitRequestFullscreen) {
          await container.webkitRequestFullscreen();
        } else if (video && video.webkitEnterFullscreen) {
          // iOS Safari native video fullscreen
          video.webkitEnterFullscreen();
        }
      } else {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen();
        }
      }
    } catch (err) {
      console.warn("Fullscreen toggle failed:", err);
    }
  };

  // Sync fullscreen state
  useEffect(() => {
    const handleFullscreenChange = () => {
      const activeFs = Boolean(document.fullscreenElement || document.webkitFullscreenElement);
      setIsFullscreen(activeFs);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
    };
  }, []);

  // Auto-hide controls when mouse is still during playback
  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 2600);
    }
  };

  const handleMouseLeave = () => {
    if (isPlaying) {
      setShowControls(false);
    }
  };

  // Keyboard accessibility
  const handleKeyDown = (e) => {
    if (e.key === " " || e.key === "k") {
      e.preventDefault();
      togglePlay();
    } else if (e.key === "f" || e.key === "F") {
      e.preventDefault();
      toggleFullscreen();
    } else if (e.key === "m" || e.key === "M") {
      e.preventDefault();
      toggleMute();
    }
  };

  // Framer Motion Animation Variants matching Serene's design system
  const fadeUp = {
    hidden: { opacity: 0, y: 35, filter: "blur(8px)" },
    show: {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  const wordContainer = {
    hidden: {},
    show: {
      transition: { staggerChildren: 0.04, delayChildren: 0.06 },
    },
  };

  const wordVariant = {
    hidden: { y: 25, opacity: 0, filter: "blur(6px)" },
    show: {
      y: 0,
      opacity: 1,
      filter: "blur(0px)",
      transition: { duration: 0.45, ease: "easeOut" },
    },
  };

  const titleWords = "Immerse Yourself in Serene Luxury".split(" ");

  return (
    <section className="serene-walkthrough-section" id="serene-walkthrough">
      <div className="container">
        {/* ================= SECTION HEADER ================= */}
        <div className="row justify-content-center text-center mb-4">
          <div className="col-lg-9 col-xl-8">
            <motion.h2
              className="approved-banks-title display-4"
              variants={wordContainer}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.6 }}
            >
              {titleWords.map((word, i) => (
                <motion.span
                  key={`walkthrough-title-${i}`}
                  variants={wordVariant}
                  style={{ display: "inline-block", marginRight: "0.32em" }}
                >
                  {word}
                </motion.span>
              ))}
            </motion.h2>

            <motion.p
              className="walkthrough-desc mb-4"
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.6 }}
            >
              Take an exclusive cinematic tour through Vessella Serene. Experience the perfect harmony of contemporary architecture, expansive sun-drenched courtyards, lush botanical landscapes, and world-class clubhouse amenities crafted for an unhurried, elevated life in Narsingi.
            </motion.p>
          </div>
        </div>

        {/* ================= FULL SCREEN VIDEO PLAYER ================= */}
        <motion.div
          className="walkthrough-player-outer"
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
        >
          <div
            ref={containerRef}
            className={`walkthrough-player-frame ${isFullscreen ? "is-fullscreen" : ""}`}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onDoubleClick={toggleFullscreen}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            role="region"
            aria-label="Vessella Serene Walkthrough Video Player"
          >


            {/* Video Element */}
            <video
              ref={videoRef}
              className="walkthrough-video-element"
              poster={posterImg}
              preload="metadata"
              playsInline
              onClick={togglePlay}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onEnded={() => setIsPlaying(false)}
            >
              <source src={videoSrc} type="video/mp4" />
              <source src={videoFallbackSrc} type="video/mp4" />
              Your browser does not support the video tag.
            </video>

            {/* Center Animated Play/Pause Overlay */}
            <div
              className={`walkthrough-center-overlay ${isPlaying ? "playing-hidden" : ""}`}
              onClick={togglePlay}
            >
              <div className="walkthrough-play-pulse-wrap">
                <span className="walkthrough-ripple ripple-1"></span>
                <span className="walkthrough-ripple ripple-2"></span>
                <div
                  className="walkthrough-play-circle-btn"
                  aria-label={isPlaying ? "Pause Video Tour" : "Play Video Tour"}
                >
                  {isPlaying ? (
                    <Pause size={34} fill="white" />
                  ) : (
                    <Play size={34} fill="white" className="walkthrough-play-icon" />
                  )}
                </div>
              </div>
              <div className="walkthrough-play-label">
                {hasStarted ? (isPlaying ? "Pause Tour" : "Resume Tour") : "Watch Full Walkthrough"}
              </div>
              <div className="walkthrough-play-subtext">
                {hasStarted ? "Click video to toggle playback" : "Click to Play • Double-click for Full Screen"}
              </div>
            </div>

            {/* Bottom Glassmorphic Control Bar */}
            <div
              className={`walkthrough-controls-bar ${showControls || !isPlaying ? "show-controls" : ""}`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Timeline Scrubber */}
              <div
                className="walkthrough-timeline-container"
                onClick={handleSeek}
                title="Seek Timeline"
              >
                <div
                  className="walkthrough-timeline-progress"
                  style={{ width: `${progress}%` }}
                >
                  <span className="walkthrough-timeline-scrubber"></span>
                </div>
              </div>

              {/* Controls Row */}
              <div className="walkthrough-controls-row">
                {/* Left Controls */}
                <div className="walkthrough-controls-left">
                  <button
                    className="walkthrough-ctrl-btn"
                    onClick={togglePlay}
                    title={isPlaying ? "Pause (Space)" : "Play (Space)"}
                    aria-label={isPlaying ? "Pause" : "Play"}
                  >
                    {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                  </button>

                  <div className="walkthrough-volume-wrap">
                    <button
                      className="walkthrough-ctrl-btn"
                      onClick={toggleMute}
                      title={isMuted ? "Unmute (m)" : "Mute (m)"}
                      aria-label={isMuted ? "Unmute" : "Mute"}
                    >
                      {isMuted || volume === 0 ? (
                        <VolumeX size={20} />
                      ) : (
                        <Volume2 size={20} />
                      )}
                    </button>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.05"
                      value={isMuted ? 0 : volume}
                      onChange={handleVolumeChange}
                      className="walkthrough-volume-slider"
                      aria-label="Volume Slider"
                    />
                  </div>

                  <span className="walkthrough-time-display">
                    {currentTime} / {duration}
                  </span>
                </div>

                {/* Right Controls */}
                <div className="walkthrough-controls-right">
                  <button
                    className="walkthrough-fullscreen-btn border-0"
                    onClick={toggleFullscreen}
                    title={isFullscreen ? "Exit Full Screen (f)" : "Full Screen (f)"}
                    aria-label={isFullscreen ? "Exit Full Screen" : "Full Screen"}
                  >
                    {isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />}
                    <span>{isFullscreen ? "Exit Fullscreen" : "Full Screen"}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
